import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  // Quick task 260512-q7q — focus-visible aligned with the globals.css
  // contract (ring/60 + ring-offset-background) so every focusable element
  // speaks the same focus language. Transition list explicit so callers can
  // use `hover:-translate-y-px` for a subtle lift, and the default
  // transition speed (150ms) feels intentional rather than snappy.
  "inline-flex shrink-0 items-center justify-center gap-2 rounded-md text-sm font-medium whitespace-nowrap transition-[background-color,box-shadow,opacity,color,transform] duration-150 outline-none focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        // Quick task 260512-q7q — primary CTA gets the elevation story:
        // resting elev-1, hover lifts to elev-2, active presses back down.
        // Color story untouched (coral primary).
        default:
          "bg-primary text-primary-foreground shadow-elev-1 hover:bg-primary/92 hover:shadow-elev-2 active:translate-y-px active:shadow-elev-1",
        destructive:
          "bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:bg-destructive/60 dark:focus-visible:ring-destructive/40",
        // Phase 7 D-10 / 07-RESEARCH §4b — `danger` is a sibling to
        // `destructive` but uses the warm-coral-paired red
        // (`--danger` / #C0392B) instead of the system red
        // (`--destructive` / #DC2626). Outlined pill aesthetic for sign-out
        // + other non-system destructive affordances (account deletion,
        // leave course, cancel consultation). The `aria-invalid` rules on
        // the cva base still point at `--destructive` (form-validation
        // red); `danger` is a styling variant, not a semantic invalid
        // signal. Tailwind v4 strips the `--color-` prefix when emitting
        // utilities, so the @theme alias `--color-danger` produces
        // `text-danger` / `bg-danger-soft` / `border-danger`.
        danger:
          "border border-border/70 bg-background text-danger shadow-elev-1 hover:border-danger hover:bg-danger-soft",
        // Quick task 260512-q7q — neutral outline now hovers to muted (gray)
        // instead of accent (coral). Coral-on-hover was wrong UX for the
        // TopNav "Log in" outline button — it suggested destructive when the
        // action is benign navigation. Softer border too.
        outline:
          "border border-border/70 bg-background shadow-elev-1 hover:bg-muted hover:border-border dark:border-input dark:bg-input/30 dark:hover:bg-input/50",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        xs: "h-6 gap-1 rounded-md px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-8 gap-1.5 rounded-md px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
        "icon-xs": "size-6 rounded-md [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
        // UI-SPEC §Spacing → Touch targets: 44px tall on mobile (WCAG 2.5.5)
        touch: "h-11 px-5 has-[>svg]:px-4 text-base",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot.Root : "button"

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
